import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useRef,
} from 'react';
import { Dialog, Classes, Button, InputGroup, Intent } from '@blueprintjs/core';
import { AppToaster } from '@/components';

/**
 * Modal-based replacements for `window.alert`, `window.confirm`,
 * `window.prompt`. Use via `useDialog()`.
 *
 *  - `alert(message, intent?)` → toast (non-blocking) for short notices.
 *  - `confirm(message, title?)` → modal with OK/Cancel, returns Promise<boolean>.
 *  - `prompt(message, defaultValue?, title?)` → modal with input, returns Promise<string|null>.
 */

type AlertIntent = 'success' | 'warning' | 'danger' | 'primary' | 'none';

interface DialogContextShape {
  alert: (message: string, intent?: AlertIntent) => void;
  confirm: (message: string, title?: string) => Promise<boolean>;
  prompt: (
    message: string,
    defaultValue?: string,
    title?: string,
    required?: boolean,
  ) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextShape | null>(null);

interface ConfirmState {
  open: boolean;
  title?: string;
  message: string;
  resolve?: (value: boolean) => void;
}

interface PromptState {
  open: boolean;
  title?: string;
  message: string;
  value: string;
  required?: boolean;
  resolve?: (value: string | null) => void;
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    open: false,
    message: '',
  });
  const [promptState, setPromptState] = useState<PromptState>({
    open: false,
    message: '',
    value: '',
  });
  const promptInputRef = useRef<HTMLInputElement | null>(null);

  const alert = useCallback(
    (message: string, intent: AlertIntent = 'primary') => {
      const intentMap: Record<AlertIntent, Intent> = {
        success: Intent.SUCCESS,
        warning: Intent.WARNING,
        danger: Intent.DANGER,
        primary: Intent.PRIMARY,
        none: Intent.NONE,
      };
      AppToaster.show({
        message,
        intent: intentMap[intent],
        timeout: intent === 'danger' || intent === 'warning' ? 8000 : 4000,
      });
    },
    [],
  );

  const confirm = useCallback(
    (message: string, title?: string) =>
      new Promise<boolean>((resolve) => {
        setConfirmState({ open: true, title, message, resolve });
      }),
    [],
  );

  const prompt = useCallback(
    (message: string, defaultValue = '', title?: string, required = false) =>
      new Promise<string | null>((resolve) => {
        setPromptState({
          open: true,
          title,
          message,
          value: defaultValue,
          required,
          resolve,
        });
      }),
    [],
  );

  const closeConfirm = (result: boolean) => {
    confirmState.resolve?.(result);
    setConfirmState({ open: false, message: '' });
  };

  const closePrompt = (result: string | null) => {
    promptState.resolve?.(result);
    setPromptState({ open: false, message: '', value: '' });
  };

  // When the caller marked the prompt as required, block confirming with an
  // empty (whitespace-only) value: the OK button is disabled and Enter is inert.
  const promptRequiredEmpty =
    !!promptState.required && promptState.value.trim() === '';

  return (
    <DialogContext.Provider value={{ alert, confirm, prompt }}>
      {children}

      <Dialog
        isOpen={confirmState.open}
        title={confirmState.title ?? 'Confirmer'}
        onClose={() => closeConfirm(false)}
        canEscapeKeyClose
        canOutsideClickClose
      >
        <div className={Classes.DIALOG_BODY}>
          <p style={{ whiteSpace: 'pre-wrap' }}>{confirmState.message}</p>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => closeConfirm(false)}>Annuler</Button>
            <Button
              intent={Intent.PRIMARY}
              autoFocus
              onClick={() => closeConfirm(true)}
            >
              Confirmer
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={promptState.open}
        title={promptState.title ?? 'Saisie'}
        onClose={() => closePrompt(null)}
        canEscapeKeyClose
        canOutsideClickClose
      >
        <div className={Classes.DIALOG_BODY}>
          <p style={{ whiteSpace: 'pre-wrap' }}>{promptState.message}</p>
          <InputGroup
            inputRef={(el) => {
              promptInputRef.current = el;
              if (el && promptState.open) setTimeout(() => el.focus(), 50);
            }}
            value={promptState.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPromptState((s) => ({ ...s, value: e.target.value }))
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && !promptRequiredEmpty)
                closePrompt(promptState.value);
              if (e.key === 'Escape') closePrompt(null);
            }}
            autoFocus
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => closePrompt(null)}>Annuler</Button>
            <Button
              intent={Intent.PRIMARY}
              disabled={promptRequiredEmpty}
              onClick={() => closePrompt(promptState.value)}
            >
              OK
            </Button>
          </div>
        </div>
      </Dialog>
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogContextShape {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    // Fallback to native if provider missing — shouldn't happen in app, but
    // protects unit tests.
    return {
      alert: (msg: string) => window.alert(msg),
      confirm: (msg: string) => Promise.resolve(window.confirm(msg)),
      prompt: (msg: string, def = '') =>
        Promise.resolve(window.prompt(msg, def)),
    };
  }
  return ctx;
}
